import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    Typography,
    Chip,
    LinearProgress,
    Tab,
    Tabs,
    Tooltip,
    TextField,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import FunctionCard from './components/FunctionCard';
import DeploymentModal from './components/DeploymentModal';
import MetricsChart from './components/MetricsChart';
import ExecutionHistory from './components/ExecutionHistory';
import PythonTestExample from './components/PythonTestExample';
import EmbeddedJupyter from './components/EmbeddedJupyter';

// icons
import {
    IconCode,
    IconRocket,
    IconRefresh,
    IconSearch,
    IconChartLine,
    IconHistory,
    IconPlus,
    IconFunction,
    IconCircleCheck,
    IconAlertCircle,
    IconBrandPython,
    IconBrandPython as IconBrandJupyter
} from '@tabler/icons-react';

// ==============================|| LAMBDA DASHBOARD ||============================== //

const LambdaDashboard = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { functions, metrics, filters, loading, selectedFunction } = useSelector((state) => state.lambdaFunctions);

    const [tabValue, setTabValue] = useState(0);
    const [deployModalOpen, setDeployModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedExample, setSelectedExample] = useState('basic');

    // Filter functions based on search and status
    const filteredFunctions = functions.filter((func) => {
        const matchesSearch =
            func.name.toLowerCase().includes(searchTerm.toLowerCase()) || func.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || func.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleRefresh = () => {
        // Simulate refresh
        dispatch({ type: 'lambdaFunctions/setLoading', payload: true });
        setTimeout(() => {
            dispatch({ type: 'lambdaFunctions/setLoading', payload: false });
        }, 1000);
    };

    // Status card data
    const statusCards = [
        {
            title: 'Total Functions',
            value: metrics.totalFunctions,
            icon: <IconFunction size={20} />,
            color: theme.palette.primary.main
        },
        {
            title: 'Deployed',
            value: metrics.deployedFunctions,
            icon: <IconCircleCheck size={20} />,
            color: theme.palette.success.main
        },
        {
            title: 'Failed',
            value: metrics.failedFunctions,
            icon: <IconAlertCircle size={20} />,
            color: theme.palette.error.main
        },
        {
            title: 'Total Executions (24h)',
            value: metrics.totalExecutions24h.toLocaleString(),
            icon: <IconChartLine size={20} />,
            color: theme.palette.info.main
        }
    ];

    return (
        <>
            <MainCard
                title={
                    <Box display="flex" alignItems="center" gap={1}>
                        <IconCode />
                        <Typography variant="h3">Lambda Functions</Typography>
                        <Chip
                            label="Beta"
                            size="small"
                            sx={{
                                bgcolor: theme.palette.warning.light,
                                color: theme.palette.warning.dark,
                                fontWeight: 600
                            }}
                        />
                    </Box>
                }
                secondary={
                    <Box display="flex" gap={1}>
                        <Tooltip title="Refresh">
                            <IconButton onClick={handleRefresh} disabled={loading}>
                                <IconRefresh size={20} />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="contained"
                            startIcon={<IconRocket size={18} />}
                            onClick={() => setDeployModalOpen(true)}
                            sx={{
                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                '&:hover': {
                                    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`
                                }
                            }}
                        >
                            Deploy Functions
                        </Button>
                    </Box>
                }
            >
                {loading && <LinearProgress sx={{ mb: 2 }} />}

                {/* Status Cards */}
                <Grid container spacing={gridSpacing} sx={{ mb: 3 }}>
                    {statusCards.map((card, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Card
                                sx={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        transition: 'transform 0.3s ease-in-out'
                                    }
                                }}
                            >
                                <CardContent>
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Box>
                                            <Typography color="textSecondary" gutterBottom variant="subtitle2">
                                                {card.title}
                                            </Typography>
                                            <Typography variant="h3" component="div">
                                                {card.value}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                backgroundColor: `${card.color}20`,
                                                borderRadius: '12px',
                                                p: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {React.cloneElement(card.icon, { style: { color: card.color } })}
                                        </Box>
                                    </Box>
                                </CardContent>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        height: '4px',
                                        backgroundColor: card.color,
                                        opacity: 0.3
                                    }}
                                />
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Filter Section */}
                <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        placeholder="Search functions..."
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ flexGrow: 1, maxWidth: 400 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IconSearch size={18} />
                                </InputAdornment>
                            )
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                            <MenuItem value="all">All Status</MenuItem>
                            <MenuItem value="deployed">Deployed</MenuItem>
                            <MenuItem value="deploying">Deploying</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <IconCode size={18} />
                                    Functions
                                </Box>
                            }
                        />
                        <Tab
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <IconChartLine size={18} />
                                    Metrics
                                </Box>
                            }
                        />
                        <Tab
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <IconHistory size={18} />
                                    Executions
                                </Box>
                            }
                        />
                        <Tab
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <IconBrandPython size={18} />
                                    Python Examples
                                </Box>
                            }
                        />
                        <Tab
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <IconBrandJupyter size={18} />
                                    Jupyter
                                </Box>
                            }
                        />
                    </Tabs>
                </Box>

                {/* Tab Content */}
                {tabValue === 0 && (
                    <Grid container spacing={gridSpacing}>
                        {filteredFunctions.map((func) => (
                            <Grid item xs={12} md={6} lg={4} key={func.id}>
                                <FunctionCard function={func} />
                            </Grid>
                        ))}
                        {filteredFunctions.length === 0 && (
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        py: 8,
                                        backgroundColor: theme.palette.grey[100],
                                        borderRadius: 2
                                    }}
                                >
                                    <IconFunction size={48} style={{ opacity: 0.3 }} />
                                    <Typography variant="h4" sx={{ mt: 2, opacity: 0.5 }}>
                                        No functions found
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.5 }}>
                                        Deploy your first function to get started
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        startIcon={<IconPlus size={18} />}
                                        onClick={() => setDeployModalOpen(true)}
                                        sx={{ mt: 3 }}
                                    >
                                        Deploy Function
                                    </Button>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                )}

                {tabValue === 1 && (
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12}>
                            <MetricsChart functions={filteredFunctions} />
                        </Grid>
                    </Grid>
                )}

                {tabValue === 2 && (
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12}>
                            <ExecutionHistory />
                        </Grid>
                    </Grid>
                )}

                {tabValue === 3 && (
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h4" gutterBottom>
                                        Python Lambda Function Examples
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <FormControl fullWidth sx={{ mb: 3 }}>
                                            <InputLabel>Select Example Type</InputLabel>
                                            <Select
                                                value={selectedExample}
                                                label="Select Example Type"
                                                onChange={(e) => setSelectedExample(e.target.value)}
                                            >
                                                <MenuItem value="basic">Basic Function</MenuItem>
                                                <MenuItem value="calculation">Business Logic</MenuItem>
                                                <MenuItem value="dataProcessing">Data Processing</MenuItem>
                                                <MenuItem value="apiIntegration">API Integration</MenuItem>
                                                <MenuItem value="errorHandling">Error Handling</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <PythonTestExample selectedExample={selectedExample} />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {tabValue === 4 && (
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12}>
                            <EmbeddedJupyter
                                projectName={localStorage.getItem('project_name') || 'default'}
                                onFunctionDeploy={(functionData) => {
                                    console.log('Function deployed from Jupyter:', functionData);
                                    // Handle function deployment
                                    // You can dispatch Redux actions here to update the functions list
                                }}
                            />
                        </Grid>
                    </Grid>
                )}
            </MainCard>

            {/* Deployment Modal */}
            <DeploymentModal open={deployModalOpen} onClose={() => setDeployModalOpen(false)} />
        </>
    );
};

export default LambdaDashboard;
